import { useEffect, useState } from 'react'
import Highcharts from 'highcharts'
import dateFnsFormat from 'date-fns/format'
import { InnerLoader } from 'src/components/InnerLoader'
import defaultConfig from 'src/components/TimeLineChart/config'
import { TimeLineChart } from 'src/components/TimeLineChart'
import { ChartFormat } from 'src/components/TimeLineChart/components/ChartFormat'
import useWidth from 'src/hooks/useWidth'
import { ColorScheme } from 'src/components/CoinChart/config'
import {
  EXPORT_COLORS,
  EXPORT_DATE_FORMAT,
  exportLogoBright,
  exportLogoDark,
} from 'src/components/CoinChart/helpers'
import {
  getXAxisConfig,
  getTooltipConfig,
  getPlotOptionsConfig,
  getSeriesConfig,
  getChartConfig,
  getYAxisConfig,
} from './configHelpers'
import { getExportTitleConfig } from './formatHelpers'
import styles from './styles.module.scss'
import { combinedAnalytics } from 'src/utils/common'

let IS_SAFARI: boolean

if (process.env.IS_BROWSER) {
  IS_SAFARI = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
}

interface DataProvider {
  x: number
  y: number
}

interface ChartDescription {
  name: string
  label: string
  labelClassName?: string
  labelActiveClassName?: string
  labelOrder: number
  title?: string
  isAxis: boolean
  mc?: number
}

interface State {
  DataProviders?: DataProvider[][]
  navigatorData?: DataProvider[]
  loading?: boolean
  isExludeBitcoin?: boolean
  minDate?: number
}

interface Range {
  from?: number
  to?: number
}

export const VolumeChart = () => {
  const { isMobile } = useWidth()
  const [state, setState] = useState<State>({
    DataProviders: [],
    navigatorData: [],
    loading: true,
    isExludeBitcoin: false,
    minDate: 0,
  })
  const [initialLoading, setInitialLoading] = useState(true)
  const [range, setRange] = useState<Range>({})

  const charts: ChartDescription[] = [
    {
      name: 'marketcap',
      label: 'Market cap',
      labelClassName: 'Chart-legend__item--GlobalMarketCap',
      labelActiveClassName: 'Chart-legend__item--GlobalMarketCapActive',
      labelOrder: 0,
      title: 'Market Cap',
      isAxis: true,
    },
    {
      name: 'volume',
      label: 'Volume 24h',
      labelClassName: 'Chart-legend__item--volume',
      labelActiveClassName: 'Chart-legend__item--volumeActive',
      labelOrder: 1,
      isAxis: false,
    },
  ]

  useEffect(() => {
    fetchCharts(0, Date.now()).then(DataProviders => {
      const navigatorData = DataProviders[0].map((i: DataProvider) => ({
        x: i.x,
        y: i.y,
      }))

      setState({
        DataProviders,
        navigatorData,
        loading: false,
        minDate: findMin(DataProviders),
      })
      setInitialLoading(false)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchCharts = async (
    from: number,
    to: number
  ): Promise<DataProvider[][]> => {
    const { isExludeBitcoin } = state

    const result = await fetch(
      `/site-api/global?start=${Math.round(from / 1000)}&end=${Math.round(
        to / 1000
      )}${isExludeBitcoin ? '&exclude=BTC' : ''}`
    )
    const data = await result.json()

    const DataProviders = parseData(data.reverse())

    setRange({ from, to })
    return DataProviders
  }

  const findMin = (data: DataProvider[][]) => {
    return data.reduce(
      (acc, i) => (acc > i[0].x ? i[0].x : acc),
      Number.MAX_VALUE
    )
  }

  const parseData = (data: number[][]) => {
    return data.reduce(
      (acc: DataProvider[][], i: number[]) => {
        acc[0].push({
          x: i[2] * 1000,
          y: i[0],
        })
        acc[1].push({
          x: i[2] * 1000,
          y: i[1],
        })
        return acc
      },
      [[], []]
    )
  }

  const makeSize = (format: ChartFormat) => {
    switch (format) {
      case ChartFormat.FULL:
        return {
          width: 1200,
          height: 900,
        }
      case ChartFormat.HORIZONTAL:
        return {
          width: 1200,
          height: 450,
        }
      case ChartFormat.VERTICAL:
        return {
          width: 450,
          height: 900,
        }
      default:
        throw Error(`Unsupported chart format ${format}`)
    }
  }

  const generateExportConfig = (
    colorScheme: string,
    chartFormat: ChartFormat,
    from: any,
    to: any,
    yAxisConf: any
  ) => {
    if (!colorScheme) {
      return {}
    }

    const colors = EXPORT_COLORS[colorScheme]

    const fromDate = dateFnsFormat(new Date(from), EXPORT_DATE_FORMAT)
    const toDate = dateFnsFormat(new Date(to), EXPORT_DATE_FORMAT)
    return {
      yAxis: yAxisConf,
      chart: {
        backgroundColor: colors.bground,
        ...makeSize(chartFormat),
        marginRight: 30,
        events: {
          load: function (this: any) {
            if (this.options.chart.forExport) {
              const marginTop = chartFormat == ChartFormat.VERTICAL ? 110 : 85
              const marginBottom =
                chartFormat == ChartFormat.VERTICAL ? 120 : 100
              this.update({
                chart: { marginTop, marginBottom },
                navigator: { enabled: false },
              })
              Highcharts.each(this.yAxis, function (axis: any) {
                axis.update({
                  gridLineColor: colors.axis,
                  gridLineWidth: 0,
                })
              })
              this.yAxis[0].update({
                gridLineColor: colors.axis,
                gridLineWidth: 1,
              })

              this.yAxis[1].addPlotLine({
                color: colors.axis,
                width: 1,
                value: 0,
                zIndex: 0,
              })

              const logo =
                colorScheme == ColorScheme.BRIGHT
                  ? exportLogoBright
                  : exportLogoDark
              const logoLabel = this.renderer
                .label(logo, null, null, null, null, null, true)
                .css({
                  width: '200px',
                })
                .add()

              logoLabel.align(
                Highcharts.extend(logoLabel.getBBox(), {
                  verticalAlign: 'bottom',
                  align: 'right',
                  x: chartFormat == ChartFormat.VERTICAL ? -60 : -30,
                  y: -27,
                })
              )

              const dateColor =
                colorScheme == ColorScheme.BRIGHT
                  ? 'rgba(187, 188, 188, 0.75)'
                  : 'rgba(187, 188, 188, 0.5)'
              const userDateLabel = this.renderer
                .label(
                  `<div style="line-height:19px;color:${dateColor};font-size: 16.82px;font-family:&quot;Roboto&quot;, sans-serif;">
                    ${dateFnsFormat(new Date(), 'dd/MM/yyyy')}
                  </div>`,
                  null,
                  null,
                  null,
                  null,
                  null,
                  true
                )
                .css({
                  width: '300px',
                })
                .add()

              userDateLabel.align(
                Highcharts.extend(logoLabel.getBBox(), {
                  verticalAlign: 'bottom',
                  align: 'right',
                  x: chartFormat == ChartFormat.VERTICAL ? -170 : -150,
                  y: -25,
                })
              )
            }
          },
        },
      },
      title: getExportTitleConfig(fromDate, toDate, colorScheme, chartFormat),
      subtitle: {
        text: '',
      },
      legend: {
        enabled: true,
        useHTML: true,
        align: chartFormat == ChartFormat.VERTICAL ? 'center' : 'left',
        y: chartFormat == ChartFormat.VERTICAL ? -65 : -30,
        labelFormatter: function (this: any) {
          return this.name.toLowerCase() == 'volume'
            ? `${this.name} 24h`
            : this.name
        },
        itemStyle: {
          color: colorScheme == ColorScheme.DARK ? '#959596' : '#BBBCBC',
          fontSize: '16px',
          fontWeight: 'normal',
          lineHeight: '21px',
        },
        symbolHeight: 8.4,
      },
      scrollbar: {
        enabled: false,
      },
    }
  }

  const generateConfig = (
    data: DataProvider[][],
    navigatorSeries: DataProvider[],
    disabledCharts: string[],
    choosenFrame: string,
    fullScreen: boolean,
    height: number,
    width: number
  ): any => {
    const withoutVolume = disabledCharts?.includes('volume')

    const config = defaultConfig()

    config.chart = getChartConfig(config, fullScreen, isMobile, height)

    config.xAxis[0] = getXAxisConfig(config, choosenFrame, 0)

    config.tooltip = getTooltipConfig(config, isMobile, choosenFrame, width)

    config.plotOptions = getPlotOptionsConfig(config, isMobile, IS_SAFARI)

    config.yAxis[0] = getYAxisConfig(0, isMobile, data)

    config.yAxis[1] = getYAxisConfig(1, isMobile, data)

    if (withoutVolume) {
      config.yAxis[1].labels.enabled = false
      config.yAxis[1].title.enabled = false
      config.yAxis[1].gridLineColor = 'transparent'
    } else {
      config.yAxis[1].labels.enabled = true
      config.yAxis[1].title.enabled = true
      config.yAxis[1].gridLineColor = 'rgba(255, 255, 255, 0.2)'
    }

    config.series = getSeriesConfig()

    charts.map((i: ChartDescription, index: number) => {
      config.series[index].data = disabledCharts?.includes(i.name)
        ? []
        : data[index]
    })
    const isVolumeEnabled = !disabledCharts?.includes(charts[1].name)

    config.yAxis[0].height = isVolumeEnabled ? '72%' : '100%'

    config.navigator.series.data = navigatorSeries ? navigatorSeries : []
    config.navigator.height = isMobile ? 45 : 50

    return config
  }

  const onDateChange = async (valueFrom: number, valueTo: number) => {
    const DataProviders = await fetchCharts(valueFrom, valueTo)
    setState({
      ...state,
      DataProviders,
      loading: false,
      minDate: findMin(DataProviders),
    })

    return DataProviders[0]
  }

  const toggleExcludeBitcoin = async () => {
    setState({
      ...state,
      isExludeBitcoin: !state.isExludeBitcoin,
    })

    const { isExludeBitcoin } = state
    combinedAnalytics(
      'Filter',
      'Filter',
      'Interaction:chart page total',
      `BTC ${!isExludeBitcoin ? 'Include' : 'Exclude'}`
    )

    const DataProviders = await fetchCharts(range.from, range.to)
    const navigatorData = DataProviders[0].map((i: DataProvider) => ({
      x: i.x,
      y: i.y,
    }))

    setInitialLoading(false)
    setState({
      DataProviders,
      navigatorData,
      loading: false,
    })
  }

  const getAdditionalButtons = () => {
    const { isExludeBitcoin } = state
    return [
      <div
        className='Chart-legend__ExcludeBitcoin'
        key='exclude-button'
        onClick={toggleExcludeBitcoin}
      >
        <span
          style={{
            fontSize: 18,
            marginTop: -2,
            fontWeight: 100,
            marginRight: 7,
          }}
        >
          {isExludeBitcoin ? '+' : '\u00D7'}
        </span>{' '}
        {isExludeBitcoin ? 'Include Bitcoin' : 'Exclude Bitcoin'}
      </div>,
    ]
  }

  const { DataProviders, navigatorData, minDate } = state

  return (
    <div>
      <div className={styles.titleContainer}>
        <h2 className={styles.subtitle}>
          Analyzing Total Market Capitalization and Volume in USD
        </h2>
        <p className={styles.description}>
          The chart below shows the historical data of global market
          capitalization and 24h volume. The total market cap parameter is
          calculated by collectively summarizing the market capitalizations of
          all cryptocurrencies in USD.
        </p>
      </div>
      <div style={{ height: isMobile ? 420 : 500, position: 'relative' }}>
        {initialLoading ? (
          <InnerLoader />
        ) : (
          <TimeLineChart
            isMobile={isMobile}
            height={200}
            width={300}
            zoomPadding={70}
            mobileZoomPadding={0}
            onDateChange={onDateChange}
            id='coinChart'
            charts={charts}
            DataProviders={DataProviders}
            navigatorData={navigatorData}
            generateConfig={generateConfig}
            additionalButtons={getAdditionalButtons()}
            generateExportConfig={generateExportConfig}
            isDownloadDropdownInverted={true}
            minDate={minDate}
          />
        )}
      </div>
    </div>
  )
}

export default VolumeChart
